import { Check, MessageSquare, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ChatList } from 'types/api'

import { cn } from '@/lib/utils'
import { getIntlLocale } from '@/i18n/languages'

interface Props {
  item: ChatList
  onClick: (id?: string) => void
}

export function ChatConversationItem({ item, onClick }: Props) {
  const { t, i18n } = useTranslation('chat')
  const unread = !!item.has_new_messages

  const locale = getIntlLocale(i18n.language)
  const time = item.updated_at
    ? new Date(item.updated_at).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short'
      })
    : ''

  return (
    <button
      type="button"
      onClick={() => onClick(item.id)}
      className="flex w-full items-center gap-3 rounded-2xl border border-ink/[0.06] bg-white p-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.03]"
    >
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white',
          unread ? 'bg-primary' : 'bg-ink/30'
        )}
      >
        <MessageSquare className="h-5 w-5" />
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm',
            unread ? 'font-bold text-ink' : 'font-medium text-ink/80'
          )}
        >
          {item.title}
        </span>
        {time && (
          <span className="text-xs text-muted-foreground">{time}</span>
        )}
      </span>

      <span
        className="shrink-0"
        title={item.is_read ? t('form.common.seen') : t('form.common.sent')}
      >
        {item.is_read ? (
          <Check className="h-4 w-4 text-primary" />
        ) : (
          <Send className="h-4 w-4 text-ink/30" />
        )}
      </span>
    </button>
  )
}
